import { initI18n, applyTranslations, t, onLocaleChange } from "./i18n";

(function () {
  "use strict";

  async function includeSections() {
    var includeNodes = Array.prototype.slice.call(document.querySelectorAll("[data-include]"));
    if (!includeNodes.length) {
      return;
    }

    await Promise.all(
      includeNodes.map(async function (node) {
        var source = node.getAttribute("data-include");
        if (!source) {
          return;
        }

        try {
          var response = await fetch(source, { credentials: "same-origin" });
          if (!response.ok) {
            throw new Error("Include fetch failed: " + source);
          }
          node.outerHTML = await response.text();
        } catch (error) {
          node.outerHTML = "";
          console.error(error);
        }
      })
    );

    if (document.querySelector("[data-include]")) {
      await includeSections();
    }
  }

  function initScrollReveal() {
    var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var revealElements = document.querySelectorAll(".scroll-reveal");

    if (prefersReducedMotion) {
      revealElements.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }

    if (revealElements.length && "IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              observer.unobserve(entry.target);
            }
          });
        },
        { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
      );

      revealElements.forEach(function (el) {
        observer.observe(el);
      });
      return;
    }

    revealElements.forEach(function (el) {
      el.classList.add("is-visible");
    });
  }

  function initSiteNav() {
    var header = document.getElementById("site-header");
    if (!header) {
      return;
    }

    var toggle = header.querySelector("[data-nav-toggle]");
    var nav = header.querySelector("[data-nav]");
    var navLinks = nav
      ? Array.prototype.slice.call(nav.querySelectorAll('.site-nav__link'))
      : [];

    var currentPath = window.location.pathname.replace(/\/index\.html$/, "") || "/";
    var isHome = currentPath === "/" || currentPath === "";

    function setOpen(isOpen) {
      header.classList.toggle("is-open", isOpen);
      if (toggle) {
        toggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      }
    }

    function setActiveSection(sectionId) {
      if (!isHome) return;
      navLinks.forEach(function (link) {
        var href = link.getAttribute("href") || "";
        var linkSec = href.replace(/^\/?#/, "");
        var isActive = sectionId && linkSec === sectionId;
        link.classList.toggle("is-active", isActive);
        if (isActive) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    }

    // If on subpage, highlight the subpage link
    if (!isHome) {
      navLinks.forEach(function (link) {
        var href = link.getAttribute("href") || "";
        if (href === currentPath || href.replace(/\/$/, "") === currentPath.replace(/\/$/, "")) {
          link.classList.add("is-active");
          link.setAttribute("aria-current", "page");
        }
      });
    }

    if (toggle) {
      toggle.addEventListener("click", function () {
        setOpen(!header.classList.contains("is-open"));
      });
    }

    if (nav) {
      nav.querySelectorAll("a").forEach(function (link) {
        link.addEventListener("click", function (event) {
          setOpen(false);
          var href = link.getAttribute("href") || "";
          if (isHome && (href.startsWith("/#") || href.startsWith("#"))) {
            var targetId = href.replace(/^\/?#/, "");
            if (targetId) {
              var targetEl = document.getElementById(targetId);
              if (targetEl) {
                event.preventDefault();
                targetEl.scrollIntoView({ behavior: "smooth" });
                try {
                  history.pushState(null, "", "/#" + targetId);
                } catch (_) {}
              }
            }
          }
        });
      });
    }

    function onScroll() {
      header.classList.toggle("is-scrolled", window.scrollY > 12);
      if (isHome && window.scrollY < 80) {
        setActiveSection("top");
      }
    }

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    window.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    });

    if (!isHome) {
      return;
    }

    var sectionIds = navLinks
      .map(function (link) {
        var href = link.getAttribute("href") || "";
        return href.replace(/^\/?#/, "");
      })
      .filter(function (id) {
        return id && id !== "top" && !id.startsWith("/");
      });

    var sections = sectionIds
      .map(function (id) {
        return document.getElementById(id);
      })
      .filter(Boolean);

    if (!sections.length || !("IntersectionObserver" in window)) {
      return;
    }

    var visibleSections = new Map();

    var spyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            visibleSections.set(entry.target.id, entry.intersectionRatio);
          } else {
            visibleSections.delete(entry.target.id);
          }
        });

        if (window.scrollY < 80) {
          setActiveSection("top");
          return;
        }

        var activeId = null;
        var bestRatio = 0;

        visibleSections.forEach(function (ratio, id) {
          if (ratio >= bestRatio) {
            bestRatio = ratio;
            activeId = id;
          }
        });

        setActiveSection(activeId || "top");
      },
      {
        root: null,
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sections.forEach(function (section) {
      spyObserver.observe(section);
    });
  }

  function initFaq() {
    var faq = document.querySelector(".faq");
    if (!faq) {
      return;
    }

    faq.addEventListener("toggle", function (event) {
      var target = event.target;
      if (!target || target.tagName !== "DETAILS" || !target.open) {
        return;
      }

      faq.querySelectorAll("details.faq__item[open]").forEach(function (item) {
        if (item !== target) {
          item.open = false;
        }
      });
    }, true);
  }

  function initLeadForm() {
    var forms = [
      document.getElementById("provider-lead-form"),
      document.getElementById("technician-lead-form")
    ].filter(Boolean);

    if (forms.length === 0) return;

    forms.forEach(function (form) {
      var questionEl = form.querySelector(".captcha-question");
      var tokenInput = form.querySelector(".lead-captcha-token");
      var refreshBtn = form.querySelector(".captcha-refresh-btn");
      var feedbackEl = form.querySelector(".form-feedback");
      var submitBtn = form.querySelector(".form-submit-btn");

      async function loadCaptcha() {
        if (questionEl) questionEl.textContent = t("provider.captchaLoading");
        try {
          var res = await fetch("/api/v1/leads/captcha");
          if (!res.ok) throw new Error("Failed to load captcha");
          var data = await res.json();
          if (questionEl) questionEl.textContent = data.question;
          if (tokenInput) tokenInput.value = data.token;
        } catch (err) {
          if (questionEl) questionEl.textContent = t("provider.captchaLoading");
          console.error(err);
        }
      }

      if (refreshBtn) {
        refreshBtn.addEventListener("click", loadCaptcha);
      }

      // Load initial captcha for this form
      loadCaptcha();

      form.addEventListener("submit", async function (e) {
        e.preventDefault();
        if (!feedbackEl) return;

        feedbackEl.className = "form-feedback hidden";
        feedbackEl.textContent = "";

        // Read fields gracefully across different templates
        var companyNameEl = form.querySelector('input[name="company_name"]');
        var contactPersonEl = form.querySelector('input[name="name"]');
        var nipEl = form.querySelector('input[name="nip"]');
        var emailEl = form.querySelector('input[name="email"]');
        var phoneEl = form.querySelector('input[name="phone"]');
        var cityEl = form.querySelector('input[name="city"]');
        var notesSelect = form.querySelector('select[name="notes"]');
        var notesInput = form.querySelector('textarea[name="notes"], input[name="notes"]');
        var captchaInput = form.querySelector('input[name="captcha_answer"]');

        var companyName = companyNameEl ? companyNameEl.value.trim() : "";
        var contactPerson = contactPersonEl ? contactPersonEl.value.trim() : "";
        var nip = nipEl ? nipEl.value.trim() : "";
        var email = emailEl ? emailEl.value.trim() : "";
        var phone = phoneEl ? phoneEl.value.trim() : "";
        var city = cityEl ? cityEl.value.trim() : "";
        var captchaAnswer = captchaInput ? captchaInput.value.trim() : "";
        var captchaToken = tokenInput ? tokenInput.value : "";

        var consentInput = form.querySelector('input[name="consent"]');
        var consent = consentInput ? consentInput.checked : false;

        // Construct composite name (Company + Person)
        var compositeName = contactPerson;
        if (companyName) {
          compositeName = contactPerson ? (companyName + " (" + contactPerson + ")") : companyName;
        }
        if (!compositeName) compositeName = "Partner";

        // Construct composite notes (NIP + details)
        var selectedNote = "";
        if (notesSelect && notesSelect.value) {
          selectedNote = notesSelect.value;
        } else if (notesInput && notesInput.value) {
          selectedNote = notesInput.value.trim();
        }

        var notesParts = [];
        if (nip) notesParts.push("NIP: " + nip);
        if (selectedNote) notesParts.push("Specjalizacja/Asortyment: " + selectedNote);
        var compositeNotes = notesParts.join(" | ");

        var checkedServices = [];
        if (selectedNote) {
          checkedServices.push(selectedNote.substring(0, 50));
        }
        form.querySelectorAll('input[name="services"]:checked').forEach(function (cb) {
          checkedServices.push(cb.value);
        });

        var submitBtnTextSpan = submitBtn ? submitBtn.querySelector("span") : null;
        var originalBtnText = submitBtnTextSpan ? submitBtnTextSpan.textContent : "";

        if (submitBtn) {
          submitBtn.disabled = true;
          if (submitBtnTextSpan) submitBtnTextSpan.textContent = t("provider.sending");
        }

        try {
          var res = await fetch("/api/v1/leads", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              name: compositeName,
              email: email,
              phone: phone,
              city: city,
              services: checkedServices,
              notes: compositeNotes,
              consent: consent,
              captcha_token: captchaToken,
              captcha_answer: captchaAnswer
            })
          });

          var data = await res.json();
          if (!res.ok) {
            throw new Error(data.error || "Wystąpił błąd podczas wysyłania zgłoszenia.");
          }

          feedbackEl.className = "form-feedback success";
          feedbackEl.textContent = data.message;
          form.reset();
          loadCaptcha();
        } catch (err) {
          feedbackEl.className = "form-feedback error";
          feedbackEl.textContent = err.message;
          loadCaptcha();
          if (captchaInput) captchaInput.value = "";
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            if (submitBtnTextSpan) submitBtnTextSpan.textContent = originalBtnText;
          }
        }
      });
    });
  }

  function initCookieBanner() {
    var banner = document.getElementById("cookie-banner");
    var btn = document.getElementById("accept-cookies");
    if (!banner || !btn) return;

    if (!localStorage.getItem("cookies_accepted")) {
      banner.classList.remove("hidden");
    }

    btn.addEventListener("click", function () {
      localStorage.setItem("cookies_accepted", "true");
      banner.classList.add("hidden");
    });
  }

  var DESKTOP_RELEASES_API = "/api/v1/desktop/releases/latest";
  var DESKTOP_RELEASES_PAGE = "#provider-desktop";

  function detectDesktopPlatform() {
    var ua = navigator.userAgent || "";
    var platform = navigator.platform || "";
    if (/Win/i.test(platform) || /Windows/i.test(ua)) return "windows";
    if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) return "macos";
    if (/Linux/i.test(platform) || /X11/i.test(ua)) return "linux";
    return "unknown";
  }

  function isAppleSilicon() {
    try {
      if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
        // Best-effort; may reject — fall through.
      }
    } catch (e) {
      /* ignore */
    }
    // Heuristic: Apple Silicon Macs report "MacIntel" in UA still; prefer arm64 when available.
    if (navigator.userAgentData && Array.isArray(navigator.userAgentData.architecture)) {
      return navigator.userAgentData.architecture.indexOf("arm") !== -1;
    }
    return false;
  }

  function pickDesktopDownloads(assets) {
    var files = (assets || []).filter(function (asset) {
      return asset && asset.name && asset.browser_download_url && !/\.(sig|json|yml|yaml)$/i.test(asset.name);
    });

    var picks = [];

    function find(pred) {
      for (var i = 0; i < files.length; i += 1) {
        if (pred(files[i].name)) return files[i];
      }
      return null;
    }

    var winSetup = find(function (n) {
      return /setup\.exe$/i.test(n) || /_x64-setup\.exe$/i.test(n);
    });
    var winMsi = find(function (n) {
      return /\.msi$/i.test(n);
    });
    if (winSetup) {
      picks.push({
        id: "windows",
        label: "Windows",
        meta: "instalator .exe",
        icon: "desktop_windows",
        asset: winSetup
      });
    } else if (winMsi) {
      picks.push({
        id: "windows",
        label: "Windows",
        meta: "instalator .msi",
        icon: "desktop_windows",
        asset: winMsi
      });
    }

    var appImage = find(function (n) {
      return /\.AppImage$/i.test(n);
    });
    var deb = find(function (n) {
      return /\.deb$/i.test(n);
    });
    // Prefer .deb on Linux: proper icon, menu entry, executable bit after install.
    if (deb) {
      picks.push({
        id: "linux",
        label: "Linux",
        meta: "pakiet .deb (zalecany)",
        icon: "terminal",
        asset: deb
      });
    }
    if (appImage) {
      picks.push({
        id: "linux-appimage",
        label: "Linux",
        meta: "AppImage",
        icon: "terminal",
        asset: appImage,
        platformGroup: "linux"
      });
    }

    var dmgArm = find(function (n) {
      return /aarch64\.dmg$/i.test(n) || /arm64\.dmg$/i.test(n);
    });
    var dmgX64 = find(function (n) {
      return /(_x64|x86_64)\.dmg$/i.test(n);
    });
    if (dmgArm) {
      picks.push({
        id: "macos-arm",
        label: "macOS",
        meta: "Apple Silicon",
        icon: "laptop_mac",
        asset: dmgArm,
        platformGroup: "macos"
      });
    }
    if (dmgX64) {
      picks.push({
        id: "macos-intel",
        label: "macOS",
        meta: "Intel",
        icon: "laptop_mac",
        asset: dmgX64,
        platformGroup: "macos"
      });
    }

    return picks;
  }

  function initScreensCarousel() {
    var root = document.querySelector("[data-screens-carousel]");
    if (!root) return;

    var track = root.querySelector("[data-screens-track]");
    var slides = Array.prototype.slice.call(root.querySelectorAll("[data-screens-slide]"));
    var prevBtn = root.querySelector("[data-screens-prev]");
    var nextBtn = root.querySelector("[data-screens-next]");
    var dotsRoot = root.querySelector("[data-screens-dots]");
    if (!track || !slides.length) return;

    var index = 0;
    var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var autoTimer = null;
    var dots = [];

    if (dotsRoot) {
      slides.forEach(function (_slide, i) {
        var dot = document.createElement("button");
        dot.type = "button";
        dot.className = "screens-carousel__dot";
        dot.setAttribute("role", "tab");
        dot.setAttribute("aria-label", "Ekran " + (i + 1));
        dot.addEventListener("click", function () {
          goTo(i);
          restartAutoplay();
        });
        dotsRoot.appendChild(dot);
        dots.push(dot);
      });
    }

    function centerOffset(i) {
      var slide = slides[i];
      var viewport = root.querySelector(".screens-carousel__viewport");
      if (!slide || !viewport) return 0;
      var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      return slideCenter - viewport.clientWidth / 2;
    }

    function goTo(nextIndex) {
      if (!slides.length) return;
      index = ((nextIndex % slides.length) + slides.length) % slides.length;

      slides.forEach(function (slide, i) {
        slide.classList.toggle("is-active", i === index);
        slide.setAttribute("aria-hidden", i === index ? "false" : "true");
      });

      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-selected", active ? "true" : "false");
        if (active) {
          dot.setAttribute("tabindex", "0");
        } else {
          dot.setAttribute("tabindex", "-1");
        }
      });

      track.style.transform = "translate3d(" + -centerOffset(index) + "px, 0, 0)";
    }

    function stopAutoplay() {
      if (autoTimer) {
        window.clearInterval(autoTimer);
        autoTimer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (reducedMotion || slides.length < 2) return;
      autoTimer = window.setInterval(function () {
        goTo(index + 1);
      }, 5000);
    }

    function restartAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        goTo(index - 1);
        restartAutoplay();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        goTo(index + 1);
        restartAutoplay();
      });
    }

    root.addEventListener("keydown", function (event) {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goTo(index - 1);
        restartAutoplay();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goTo(index + 1);
        restartAutoplay();
      }
    });

    root.addEventListener("pointerenter", stopAutoplay);
    root.addEventListener("pointerleave", startAutoplay);
    root.addEventListener("focusin", stopAutoplay);
    root.addEventListener("focusout", function (event) {
      if (!root.contains(event.relatedTarget)) {
        startAutoplay();
      }
    });

    var touchStartX = null;
    root.addEventListener(
      "touchstart",
      function (event) {
        if (!event.changedTouches || !event.changedTouches.length) return;
        touchStartX = event.changedTouches[0].clientX;
        stopAutoplay();
      },
      { passive: true }
    );
    root.addEventListener(
      "touchend",
      function (event) {
        if (touchStartX == null || !event.changedTouches || !event.changedTouches.length) {
          startAutoplay();
          return;
        }
        var delta = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(delta) > 40) {
          goTo(index + (delta < 0 ? 1 : -1));
        }
        startAutoplay();
      },
      { passive: true }
    );

    window.addEventListener(
      "resize",
      function () {
        goTo(index);
      },
      { passive: true }
    );

    goTo(0);
    startAutoplay();
  }

  async function initDesktopDownloads() {
    var roots = document.querySelectorAll("[data-desktop-downloads]");
    if (!roots.length) return;

    try {
      var response = await fetch(DESKTOP_RELEASES_API, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      });
      if (!response.ok) {
        throw new Error("HTTP " + response.status);
      }
      var release = await response.json();
      var picks = pickDesktopDownloads(release.assets || []);
      var preferred = detectDesktopPlatform();
      if (preferred === "unknown") {
        preferred = "windows";
      }
      var preferArm = preferred === "macos" && isAppleSilicon();

      roots.forEach(function (root) {
        var linksEl = root.querySelector("[data-desktop-links]");
        var versionEl = root.querySelector("[data-desktop-version]");
        var allEl = root.querySelector("[data-desktop-all]");
        if (!linksEl) return;

        function showInstructionsFor(platform) {
          var osKey = platform;
          if (platform.indexOf("macos") !== -1) {
            osKey = "macos";
          } else if (platform.indexOf("linux") !== -1) {
            osKey = "linux";
          }
          var items = root.querySelectorAll("[data-os-instruct]");
          items.forEach(function (item) {
            var targetOs = item.getAttribute("data-os-instruct");
            if (targetOs === osKey) {
              item.removeAttribute("hidden");
            } else {
              item.setAttribute("hidden", "true");
            }
          });
        }

        showInstructionsFor(preferred);

        if (versionEl) {
          var tag = release.tag_name || release.name || "v1.6.0";
          versionEl.hidden = false;
          versionEl.setAttribute("data-raw-tag", tag);
          versionEl.setAttribute("data-draft", release.draft ? "true" : "false");
          versionEl.textContent = t("provider.currentVersion") + " " + tag + (release.draft ? " " + t("provider.draft") : "");
        }
        if (allEl) {
          allEl.href = DESKTOP_RELEASES_PAGE;
          allEl.removeAttribute("target");
          allEl.textContent = t("provider.seeDownloads");
        }

        if (!picks.length) {
          return;
        }

        linksEl.innerHTML = "";
        picks.forEach(function (pick) {
          var group = pick.platformGroup || pick.id;
          var isPreferred =
            group === preferred ||
            (preferred === "macos" &&
              ((preferArm && pick.id === "macos-arm") ||
                (!preferArm && pick.id === "macos-intel")));

          var a = document.createElement("a");
          a.className = "provider-desktop__btn" + (isPreferred ? " is-preferred" : "");
          a.href = pick.asset.browser_download_url;
          a.rel = "noopener noreferrer";
          a.setAttribute("download", pick.asset.name || "");
          a.setAttribute("data-platform", group);
          a.innerHTML =
            '<span class="material-symbols-outlined" aria-hidden="true">' +
            pick.icon +
            "</span><span>" +
            pick.label +
            '<span class="provider-desktop__meta">' +
            pick.meta +
            "</span></span>";
          linksEl.appendChild(a);
        });

        linksEl.addEventListener("click", function (e) {
          var btn = e.target.closest("[data-platform]");
          if (btn) {
            var platform = btn.getAttribute("data-platform");
            showInstructionsFor(platform);
          }
        });
      });
    } catch (error) {
      console.warn("Desktop release fetch fallback to static links:", error);
    }
  }

  function initNavMore() {
    var header = document.getElementById("site-header");
    if (!header) return;
    var moreItem = header.querySelector(".site-nav__item--more");
    if (!moreItem) return;
    var moreBtn = moreItem.querySelector("[data-nav-more]");
    if (!moreBtn) return;

    function closeMore() {
      moreItem.classList.remove("is-open");
      moreBtn.setAttribute("aria-expanded", "false");
    }

    moreBtn.addEventListener("click", function (event) {
      event.stopPropagation();
      var willOpen = !moreItem.classList.contains("is-open");
      moreItem.classList.toggle("is-open", willOpen);
      moreBtn.setAttribute("aria-expanded", willOpen ? "true" : "false");
    });

    moreItem.querySelectorAll(".site-nav__more-list a").forEach(function (link) {
      link.addEventListener("click", closeMore);
    });

    document.addEventListener("click", function (event) {
      if (!moreItem.contains(event.target)) closeMore();
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") closeMore();
    });
  }

  function initDesktopDownloadsI18n() {
    var root = document.querySelector("[data-desktop-downloads]");
    if (!root) return;
    var versionEl = root.querySelector("[data-desktop-version]");
    if (versionEl && versionEl.textContent) {
      var tag = versionEl.getAttribute("data-raw-tag") || "";
      var isDraft = versionEl.getAttribute("data-draft") === "true";
      if (tag) {
        versionEl.textContent = t("provider.currentVersion") + " " + tag + (isDraft ? " " + t("provider.draft") : "");
      }
    }
  }

  async function initTechnicianTrialPromo() {
    var nodes = document.querySelectorAll("[data-trial-promo]");
    if (!nodes.length) return;
    try {
      var res = await fetch("/api/v1/public/technician-trial", { credentials: "same-origin" });
      if (!res.ok) return;
      var data = await res.json();
      if (data && data.enabled === false) {
        nodes.forEach(function (el) {
          el.hidden = true;
        });
      }
    } catch (_) {}
  }

  function formatPln(value) {
    var n = Number(value);
    if (!isFinite(n)) return "—";
    return (
      n.toLocaleString("pl-PL", {
        minimumFractionDigits: n % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }) + " zł"
    );
  }

  function formatPercent(value) {
    var n = Number(value);
    if (!isFinite(n)) return "—";
    return (
      n.toLocaleString("pl-PL", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }) + "%"
    );
  }

  function subscriptionLabel(category) {
    var key = "provider.pricing.cat." + category;
    var translated = t(key);
    return translated === key ? category : translated;
  }

  var cachedProviderPricing = null;

  function renderProviderPricing(data) {
    if (!data) return;

    // 1. Modal / Embedded general provider pricing
    var root = document.querySelector("[data-provider-pricing]");
    if (root) {
      var loading = root.querySelector("[data-pricing-loading]");
      var errorEl = root.querySelector("[data-pricing-error]");
      var loaded = root.querySelector("[data-pricing-loaded]");
      var commissionsUl = root.querySelector("[data-pricing-commissions]");
      var subsUl = root.querySelector("[data-pricing-subscriptions]");
      if (commissionsUl && Array.isArray(data.commissions)) {
        var rows = data.commissions
          .map(function (row) {
            var name = row.product_name || row.product_code || "";
            var rate = formatPercent(row.commission_rate_percent);
            return "<li><span>" + name + "</span><strong>" + rate + "</strong></li>";
          });
        if (data.own_orders_fee) {
          var ownName = data.own_orders_fee.name || "Zlecenia własne (Trasa A+B)";
          var ownRate = formatPercent(data.own_orders_fee.commission_rate_percent);
          rows.push("<li style=\"border-top: 1px dashed rgba(249,115,22,0.3); padding-top: 0.35rem; margin-top: 0.35rem;\"><span style=\"color: #f97316;\">" + ownName + "</span><strong style=\"color: #f97316;\">" + ownRate + "</strong></li>");
        }
        commissionsUl.innerHTML = rows.join("");
      }
      if (subsUl && Array.isArray(data.subscriptions)) {
        subsUl.innerHTML = data.subscriptions
          .map(function (row) {
            var name = subscriptionLabel(row.category);
            var price = formatPln(row.price_pln);
            return "<li><span>" + name + "</span><strong>" + price + "</strong></li>";
          })
          .join("");
      }
      if (loading) loading.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (loaded) loaded.hidden = false;
    }

    // 2. B2B Fuel Suppliers page (/dla-dostawcow)
    var fuelRoot = document.querySelector("[data-b2b-fuel-pricing]");
    if (fuelRoot) {      if (Array.isArray(data.commissions) && data.commissions.length > 0) {
        var rates = data.commissions.map(function (c) {
          return Number(c.commission_rate_percent) || 0;
        });
        var minRate = Math.min.apply(null, rates);
        var maxRate = Math.max.apply(null, rates);
        var rateEl = fuelRoot.querySelector("[data-fuel-stream-a-rate]");
        if (rateEl) {
          rateEl.textContent = formatPercent(minRate) + " – " + formatPercent(maxRate);
        }

        var listEl = fuelRoot.querySelector("[data-fuel-commissions-list]");
        if (listEl) {
          var itemsHtml = data.commissions.map(function (c) {
            var name = c.product_name || c.product_code || "";
            var rate = formatPercent(c.commission_rate_percent);
            var minStr = c.min_commission_pln ? " (min. " + formatPln(c.min_commission_pln) + ")" : "";
            return "<li><span class=\"check-mark\">✓</span> <strong>" + name + ":</strong> prowizja " + rate + minStr + "</li>";
          });
          itemsHtml.push("<li><span class=\"check-mark\">✓</span> Płatności online Stripe / Klarna lub gotówka przy odbiorze</li>");
          itemsHtml.push("<li><span class=\"check-mark\">✓</span> Weryfikacja tożsamości klienta i powiadomienia SMS / Push</li>");
          itemsHtml.push("<li><span class=\"check-mark\">✓</span> Elektroniczna e-WZ z oświadczeniem akcyzowym w cenie</li>");
          itemsHtml.push("<li><span class=\"check-mark\">✓</span> 0 zł prowizji, jeśli klient nie zaakceptuje Twojej oferty</li>");
          listEl.innerHTML = itemsHtml.join("");
        }
      }
      if (data.own_orders_fee) {
        var ownRateEl = fuelRoot.querySelector("[data-fuel-stream-b-rate]");
        if (ownRateEl) {
          var pct = Number(data.own_orders_fee.commission_rate_percent) || 0;
          ownRateEl.textContent = pct === 0 ? "0 zł (0%)" : formatPercent(pct);
        }
        var ownNoteEl = fuelRoot.querySelector("[data-fuel-stream-b-note]");
        if (ownNoteEl) {
          ownNoteEl.textContent = "0 zł prowizji od sprzedaży Twojego paliwa i Twoich klientów";
        }
      }
    }

    // 2b. PUESC plans (Pro / Enterprise) — fetched from same endpoint
    var puescRoot = document.querySelector("[data-puesc-plans]");
    if (puescRoot && data.plans) {
      var loading = puescRoot.querySelector("[data-pricing-loading]");
      var errorEl = puescRoot.querySelector("[data-pricing-error]");
      var loaded = puescRoot.querySelector("[data-pricing-loaded]");
      var grid = puescRoot.querySelector("[data-puesc-plans-grid]");

      if (loading) loading.hidden = true;
      if (errorEl) errorEl.hidden = true;
      if (loaded) loaded.hidden = false;

      if (grid) {
        var plansHtml = [];
        // Basic plan (always free, shown first)
        plansHtml.push(
          '<div class="b2b-price-box">' +
            '<div class="b2b-price-box__tag" style="background: #10b981;">Promocja do końca 2026 r. · 0 zł</div>' +
            '<h3 class="b2b-price-box__title">Plan Basic</h3>' +
            '<p class="b2b-price-box__desc">Zarządzanie dostawami i import tras z Excela bez modułu PUESC.</p>' +
            '<div class="b2b-price-box__rate">0 zł / mies.</div>' +
            '<div class="b2b-price-box__note">Promocja do końca 2026 r. · 0 zł stałych opłat i 0% prowizji od Twoich klientów</div>' +
            '<ul class="b2b-price-list">' +
              '<li><span class="check-mark">✓</span> 0 zł prowizji od własnych stałych klientów (Stream B)</li>' +
              '<li><span class="check-mark">✓</span> Prowizja tylko za nowe zlecenia z rynku (Stream A)</li>' +
              '<li><span class="check-mark">✓</span> e-WZ, podpis klienta na ekranie, tarcza akcyzowa</li>' +
              '<li><span class="check-mark">✓</span> Aplikacja mobilna dla kierowcy + aplikacja na komputer PC/Mac</li>' +
              '<li><span class="check-mark">✓</span> Import arkuszy Excel / CSV i trasy mieszane</li>' +
              '<li><span class="check-mark" style="color: #9ca3af;">✗</span> <span style="color: var(--muted);">Bez automatycznego SENT / e-DD do PUESC</span></li>' +
            '</ul>' +
          '</div>'
        );

        // Pro plan
        if (data.plans.pro) {
          var pro = data.plans.pro;
          var proFeatures = (pro.features || []).map(function(f) {
            return '<li><span class="check-mark">✓</span> ' + f + '</li>';
          }).join("");
          plansHtml.push(
            '<div class="b2b-price-box b2b-price-box--featured">' +
              '<div class="b2b-price-box__tag" style="background: #0891b2;">Promocja do końca 2026 r. · PUESC</div>' +
              '<h3 class="b2b-price-box__title">Plan ' + (pro.name || 'Pro') + '</h3>' +
              '<p class="b2b-price-box__desc">Moduł fiskalny: automatyczne zgłoszenia SENT + e-DD do PUESC.</p>' +
              '<div class="b2b-price-box__rate">' + formatPln(pro.price_pln) + ' / ' + (pro.period_days || 30) + ' dni</div>' +
              '<div class="b2b-price-box__note">Stawka promocyjna do końca 2026 r. · Add-on do planu Basic</div>' +
              '<ul class="b2b-price-list">' +
                '<li><span class="check-mark">✓</span> Aplikacja mobilna dla kierowcy + aplikacja na komputer PC/Mac</li>' +
                proFeatures +
              '</ul>' +
            '</div>'
          );
        }

        // Enterprise plan
        if (data.plans.enterprise) {
          var ent = data.plans.enterprise;
          var entFeatures = (ent.features || []).map(function(f) {
            return '<li><span class="check-mark">✓</span> ' + f + '</li>';
          }).join("");
          plansHtml.push(
            '<div class="b2b-price-box">' +
              '<div class="b2b-price-box__tag" style="background: #7c3aed;">Promocja do końca 2026 r. · Premium</div>' +
              '<h3 class="b2b-price-box__title">Plan ' + (ent.name || 'Enterprise') + '</h3>' +
              '<p class="b2b-price-box__desc">Dla dużych flot i sieci składów z integracją ERP/TMS.</p>' +
              '<div class="b2b-price-box__rate">' + formatPln(ent.price_pln) + ' / ' + (ent.period_days || 30) + ' dni</div>' +
              '<div class="b2b-price-box__note">Stawka promocyjna do końca 2026 r. · Nielimitowana flota</div>' +
              '<ul class="b2b-price-list">' +
                '<li><span class="check-mark">✓</span> Aplikacja mobilna dla kierowcy + aplikacja na komputer PC/Mac</li>' +
                entFeatures +
              '</ul>' +
            '</div>'
          );
        }

        grid.innerHTML = plansHtml.join("");
      }
    }

    // 3. B2B HVAC Technicians page (/dla-serwisantow)
    var techRoot = document.querySelector("[data-b2b-tech-pricing]");
    if (techRoot && Array.isArray(data.subscriptions) && data.subscriptions.length > 0) {
      var subMap = {};
      var subPrices = [];
      data.subscriptions.forEach(function (s) {
        subMap[s.category] = s.price_pln;
        if (typeof s.price_pln === "number" && s.price_pln > 0) {
          subPrices.push(s.price_pln);
        }
      });

      var techListEl = techRoot.querySelector("[data-tech-subscriptions-list]");
      if (techListEl) {
        var techItems = data.subscriptions.map(function (s) {
          var name = subscriptionLabel(s.category);
          var price = formatPln(s.price_pln);
          return "<li><span class=\"check-mark\">✓</span> <strong>" + name + ":</strong> " + price + " / 30 dni</li>";
        });
        techItems.push("<li><span class=\"check-mark\">✓</span> <strong>Pakiet Wszystkie Kategorie (PRO):</strong> 99 zł / 30 dni</li>");
        techItems.push("<li><span class=\"check-mark\">✓</span> <strong>0% prowizji</strong> od wartości wykonywanych usług</li>");
        techItems.push("<li><span class=\"check-mark\">✓</span> Nielimitowana liczba wycen i zapytań w Twoim powiecie</li>");
        techItems.push("<li><span class=\"check-mark\">✓</span> Faktura VAT za abonament generowana automatycznie</li>");
        techListEl.innerHTML = techItems.join("");
      }

      if (subPrices.length > 0) {
        var minSub = Math.min.apply(null, subPrices);
        var maxSub = Math.max.apply(null, subPrices);
        var techRateEl = techRoot.querySelector("[data-tech-subs-rate]");
        if (techRateEl) {
          techRateEl.textContent = formatPln(minSub) + " – " + formatPln(maxSub) + " / 30 dni";
        }
      }
    }
  }

  async function initProviderPricing() {
    var hasPricingElements = document.querySelector("[data-provider-pricing], [data-b2b-fuel-pricing], [data-b2b-tech-pricing]");
    if (!hasPricingElements) return;

    var root = document.querySelector("[data-provider-pricing]");
    var loading = root ? root.querySelector("[data-pricing-loading]") : null;
    var errorEl = root ? root.querySelector("[data-pricing-error]") : null;
    var loaded = root ? root.querySelector("[data-pricing-loaded]") : null;

    try {
      var res = await fetch("/api/v1/public/provider-pricing", { credentials: "same-origin" });
      if (!res.ok) throw new Error("bad status");
      cachedProviderPricing = await res.json();
      renderProviderPricing(cachedProviderPricing);
    } catch (_) {
      if (loading) loading.hidden = true;
      if (errorEl) errorEl.hidden = false;
      if (loaded) loaded.hidden = true;
    }
  }

  onLocaleChange(function () {
    if (cachedProviderPricing) renderProviderPricing(cachedProviderPricing);
  });

  document.addEventListener("DOMContentLoaded", async function () {
    await includeSections();
    initI18n();
    initLeadForm();
    initCookieBanner();
    initSiteNav();
    initNavMore();
    initFaq();
    initScreensCarousel();
    initScrollReveal();
    initDesktopDownloads();
    initTechnicianTrialPromo();
    initProviderPricing();
  });
})();
